import { useState } from "react";
import { Menu, ShoppingBag } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useCart } from "../lib/cart";
import { scrollToId } from "../lib/smooth";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";

const links = [
  { id: "shop", label: "Shop" },
  { id: "custom", label: "Custom prints" },
  { id: "how", label: "How it works" },
  { to: "/about", id: "about", label: "About us" },
  { to: "/info", id: "info", label: "Returns & care" },
];

export const Nav = () => {
  const { count, setOpen } = useCart();
  const loc = useLocation();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  const go = (l) => {
    setMenu(false);
    if (l.to) return nav(l.to);
    if (loc.pathname !== "/") {
      nav("/");
      setTimeout(() => scrollToId(l.id), 350);
    } else scrollToId(l.id);
  };

  return (
    <header data-testid="nav-header" className="fixed top-0 inset-x-0 z-40 bg-[var(--paper)] border-b-2 border-[var(--ink)]">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16 sm:h-[72px]">
        <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button key={l.id} data-testid={`nav-link-${l.id}`} onClick={() => go(l)}
              className={`px-4 py-2 rounded-full font-semibold hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors ${l.to && loc.pathname === l.to ? "bg-[var(--yellow)]" : ""}`}>
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button data-testid="nav-menu-btn" onClick={() => setMenu(true)} aria-label="Open menu"
            className="lg:hidden w-11 h-11 rounded-full grid place-items-center bg-[var(--yellow)] chunk-sm press">
            <Menu className="w-5 h-5" />
          </button>
          <button data-testid="nav-cart-btn" onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--pink)] text-white font-bold chunk-sm press">
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Cart</span>
            <span data-testid="nav-cart-count" className="min-w-6 h-6 px-1.5 grid place-items-center rounded-full bg-white text-[var(--ink)] text-xs font-mono-x">
              {count}
            </span>
          </button>
        </div>
      </div>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="right" data-testid="mobile-menu" className="w-[85vw] max-w-sm bg-[var(--paper)] border-l-[2.5px] border-[var(--ink)]">
          <SheetTitle className="font-display font-extrabold text-2xl">Menu</SheetTitle>
          <SheetDescription className="sr-only">Site navigation</SheetDescription>
          <div className="mt-6 flex flex-col gap-3">
            {links.map((l, i) => (
              <button key={l.id} data-testid={`mobile-link-${l.id}`} onClick={() => go(l)}
                className="text-left rounded-2xl chunk-sm press px-5 py-4 font-display font-extrabold text-xl"
                style={{ background: ["var(--yellow)", "var(--pink)", "var(--cyan)", "var(--lime)", "var(--orange)"][i % 5] }}>
                {l.label}
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};
